import angular from 'angular';
import moment from 'moment';
import _ from 'lodash';
import DepsService from 'services/dependencies';

import './config/errorcodes.conf';
import DefaultDialogController from 'utils/notifications/default-dialog-controller';
import { initGrid } from 'utils/grid/grid';
import { checkAccess } from 'app/actions/app.actions';
import { setNewLabelStatus } from 'documents/actions/set-new-label-status.actions';
import * as api from 'documents/resources/documents.api';
import { changePageTitle } from 'auth/actions/session.actions';
import DocumentsFilterComponent from './components/documents-filter/documents-filter-component';
import removeDocumentConfirmTemplate from './views/document-delete-confirm.html';
import editDocumentModalTemplate from './views/edit-document-modal.html';
import previewDocumentDialogTemplate from './views/preview-document-dialog.html';
import categoryDialogTemplate from './components/documents-management/category-dialog/category-dialog.html';
import uploadFormModalTemplate from './components/documents-management/upload-form-modal/upload-form-modal.html';
import UploadFormModalController from './components/documents-management/upload-form-modal/upload-form-modal-controller';
import CategoryDialogController from './components/documents-management/category-dialog/category-dialog-controller';

import documentsTemplate from './documents.html';

import {
  createCategory,
  downloadDocument,
  getCategories,
  getDocuments,
  getTags,
  removeCategory,
  removeDocument,
  saveDocument,
  updateCategory,
  uploadDocument,
} from './actions/documents.actions';

const { tableView, gridView, fileFormats } = getConsts();

class DocumentEditController extends DefaultDialogController {
  constructor() {
    super();
  }

  addTag(tag) {
    if (!tag) {
      return;
    }

    if (!tag || tag.length > this.keywordMaxLength) {
      return this.editForm.keywords.$setValidity('keyword-max-length', false);
    }

    this.documentTmp.tags = this.documentTmp.tags.concat(tag);
    this.tagName = null;
  }

  removeTag(index) {
    this.documentTmp.tags.splice(index, 1);
  }
}

const defaultSort = 'Publish Date (descending)';

const mapStateToThis = ({ session, config, documents }) => {
  const { locale, isAdmin } = session;
  const { locales } = config;
  const { newLabel, categories, files, isLoading, meta } = documents;

  return {
    session,
    locale,
    locales,
    newLabel,
    categories,
    isLoading,
    meta,
    isAdmin,
    documents: files,
  };
};

const mapDispatchToThis = {
  setNewLabelStatus,
  getDocuments,
  getTags,
  removeDocument,
  downloadDocument,
  getCategories,
  createCategory,
  updateCategory,
  removeCategory,
  checkAccess,
  saveDocument,
  uploadDocument,
};

// eslint-disable-next-line no-undef
@Inject('$window', '$ngRedux', '$state', 'gettextCatalog', 'Notifications', 'Responsive')
class DocumentsController {
  constructor() {
    this.unsubscribe = this.$ngRedux.connect(mapStateToThis, mapDispatchToThis)(this);
  }

  $onInit() {
    // check access permissions
    this.canEditDocuments = this.checkAccess('documents.editDocuments');
    this.canUploadDocument = this.checkAccess('documents.uploadDocument');

    this.dropdownType = 'label';
    this.currentView = this.Responsive.isMobile ? tableView : gridView;
    this.views = this.getViews();
    this.pageHeader = this.gettextCatalog.getString('Documents');
    this.gridHeader = this.gettextCatalog.getString('Documents');
    this.uploadFormHeader = this.gettextCatalog.getString('Upload Document');
    this.headers = this.getColumnHeaders()[`admin_${this.canEditDocuments}`];
    this.sortByOptions = this.getSortByOptions();
    this.sortBy = defaultSort;
    this.setDefaultPreview();

    this.defaultParams = {
      [DepsService.config.sortColumnKey]: 'publishingStartDate',
      [DepsService.config.directionKey]: 'desc',
      [DepsService.config.pageSizeKey]: DepsService.config.defaultPageSize[2],
    };

    this.defaultFilters = {
      tag: [],
      locale: [this.locale],
    };

    this.gridModel = initGrid(this.getDocuments, this.defaultParams);
    this.gridModel.pagingModel.filters = _.cloneDeep(this.defaultFilters);

    Promise.all([this.getCategories(), this.getTags()]).then(() => {
      this.search();
    });

    if (this.newLabel) {
      this.setNewLabelStatus();
    }
  }

  $onDestroy() {
    this.unsubscribe();
  }

  $onChanges(changes) {
    if (!this.Responsive.isDesktop) {
      this.currentView = tableView;
    }
  }

  clearFilters() {
    this.gridModel = initGrid(this.getDocuments, this.defaultParams);
    this.gridModel.pagingModel.filters = _.cloneDeep(this.defaultFilters);
    this.search();
  }

  getColumnHeaders() {
    return {
      admin_false: [
        {
          text: this.gettextCatalog.getString('Name'),
          field: 'guiName',
          sortable: true,
        },
        {
          text: this.gettextCatalog.getString('Category'),
          field: 'documentCategory.translations.name',
          sortable: true,
        },
        {
          text: this.gettextCatalog.getString('Published'),
          field: 'publishingStartDate',
          sortable: true,
        },
        null,
        {
          text: this.gettextCatalog.getString('File'),
          field: 'name',
          sortable: true,
        },
        {
          text: this.gettextCatalog.getString('Type'),
          field: 'extension',
          sortable: true,
        },
        {
          text: this.gettextCatalog.getString('Actions'),
        },
      ],
      admin_true: [
        {
          text: this.gettextCatalog.getString('Name'),
          field: 'guiName',
          sortable: true,
        },
        {
          text: this.gettextCatalog.getString('Category'),
          field: 'documentCategory.translations.name',
          sortable: true,
        },
        {
          text: this.gettextCatalog.getString('Published'),
          field: 'publishingStartDate',
          sortable: true,
        },
        {
          text: this.gettextCatalog.getString('Expires'),
          field: 'publishingEndDate',
          sortable: true,
        },
        {
          text: this.gettextCatalog.getString('File'),
          field: 'name',
          sortable: true,
        },
        {
          text: this.gettextCatalog.getString('Type'),
          field: 'extension',
          sortable: true,
        },
        {
          text: this.gettextCatalog.getString('Actions'),
        },
      ],
    };
  }

  getSortByOptions() {
    return [
      {
        label: this.gettextCatalog.getString('Name (A-Z)'),
        column: 'guiName',
        ascending: 'true',
      },
      {
        label: this.gettextCatalog.getString('Name (Z-A)'),
        column: 'guiName',
        ascending: 'false',
      },
      {
        label: this.gettextCatalog.getString('Publish Date (ascending)'),
        column: 'publishingStartDate',
        ascending: 'true',
      },
      {
        label: this.gettextCatalog.getString('Publish Date (descending)'),
        column: 'publishingStartDate',
        ascending: 'false',
      },
    ];
  }

  setDefaultPreview() {
    this.preview = {
      previewMode: false,
      previewData: {},
    };
  }

  getViews() {
    return [
      {
        name: this.gettextCatalog.getString('Grid'),
        id: 'grid',
      },
      {
        name: this.gettextCatalog.getString('List'),
        id: 'table',
      },
    ];
  }

  setViewActive(view) {
    this.currentView = view === gridView ? gridView : tableView;
  }

  isViewActive(view) {
    return this.currentView === view;
  }

  isGridViewActive() {
    return this.currentView === gridView;
  }

  isTableViewActive() {
    return this.currentView === tableView;
  }

  getGridIconClass(document) {
    return fileFormats[document.type] ? fileFormats[document.type].icon : 'fa-file';
  }

  checkIfAllowPreview(document) {
    return fileFormats[document.type] ? fileFormats[document.type].allowPreview : false;
  }

  sortCategories() {
    return (category) => _.get(category, `translations[${this.locale}].name`);
  }

  search() {
    this.gridModel.applyFilters();
  }

  onSaveDocument(document, documentUpdated, form) {
    form.$setSubmitted();
    this.validateDates(documentUpdated);

    if (form.$valid && !this.dateError) {
      form.$setPristine();
      form.$setUntouched();

      const data = { ...document, ...documentUpdated };

      // check if tag was deleted
      const deletedTag = this.checkIfTagDeleted(document, documentUpdated);

      this.saveDocument(data)
        .then(() => this.Notifications.alertSuccess(this.gettextCatalog.getString('Document saved successfully.')))
        .then(() => this.confirm())
        .then(() => (deletedTag ? (this.filters.tag = []) : null));
    }
  }

  checkIfTagDeleted(document, documentUpdated) {
    const documentTags = document.tags;
    const documentUpdatedTags = documentUpdated.tags;

    const intersection = _.intersection(documentTags, documentUpdatedTags);
    return intersection.length !== documentTags.length;
  }

  onRemoveDocument(document) {
    this.Notifications.showComplexDialog({
      template: removeDocumentConfirmTemplate,
    }).result.then((result) => {
      if (result) {
        this.removeDocument(document)
          .then(() => this.Notifications.alertSuccess(this.gettextCatalog.getString('Document removed successfully.')))
          .then(() => this.search());
      }
    });
  }

  onUploadDocument(document) {
    return this.uploadDocument(document)
      .then(() => this.Notifications.alertSuccess(this.gettextCatalog.getString('Document uploaded successfully')))
      .catch(() => this.Notifications.alertError(this.gettextCatalog.getString('Error while uploading document')));
  }

  onDownloadDocument(document) {
    return this.downloadDocument(document);
  }

  onRemoveCategory(category) {
    return this.removeCategory(category).then(() =>
      this.Notifications.alertSuccess(this.gettextCatalog.getString('Category removed successfully'))
    );
  }

  openEditDocumentDialog(doc, categories) {
    const documentTmp = {
      title: doc.title,
      categoryId: doc.categoryId ? doc.categoryId.toString() : null,
      tags: _.cloneDeep(doc.tags),
      locale: doc.locale,
      publishingStartDate: doc.publishingStartDate,
      publishingEndDate: doc.publishingEndDate ? doc.publishingEndDate : undefined,
      thumbnailData: doc.thumbnail ? [{ name: 'thumbnail' }] : undefined,
    };

    const { getTags } = this;
    const { search } = this;

    this.Notifications.showComplexDialog({
      backdrop: 'static',
      template: editDocumentModalTemplate,
      controller: DocumentEditController,
      resolve: {
        doc: () => doc,
        config: () => this.api.config,
        serverTime: () => this.api.config.serverTime,
        documentTmp: () => documentTmp,
        categories: () => this.categories,
        locales: () => this.locales,
        locale: () => this.locale,
        keywordMaxLength: () => this.api.config.documents.keywordMaxLength,
        sortCategories: () => this.sortCategories(),
        onSaveDocument: () => this.onSaveDocument,
        validateDates: () => this.validateDates,
        saveDocument: () => this.saveDocument,
        filters: () => this.gridModel.pagingModel.filters,
        checkIfTagDeleted: () => this.checkIfTagDeleted,
        filter: () => this.search,
        Notifications: () => this.Notifications,
        gettextCatalog: () => this.gettextCatalog,
      },
    }).result.then(() => {
      this.getTags();
      this.getDocuments(this.gridModel.pagingModel);
    });
  }

  validateDates(document) {
    if (!document.publishingEndDate) {
      this.dateError = false;
      return;
    }

    const dateFrom = moment(document.publishingStartDate);
    const dateTo = moment(document.publishingEndDate);

    if (dateFrom.isAfter(dateTo, 'day')) {
      this.dateError = true;
    } else {
      this.dateError = false;
    }
  }

  async showDocumentPreview(file) {
    try {
      await api.prefetchDocument(file.id).promise.then((result) => {
        const type = file.fileType;
        const blob = type === 'pdf' ? new Blob([result.data], { type: 'application/pdf' }) : new Blob([result.data]);
        const url = (this.$window.URL || this.$window.webkitURL).createObjectURL(blob);
        return this.openPreview(url, type);
      });
    } catch (error) {
      console.error('DOCUMENTS_COMPONENT', error);
    }
  }

  openPreview(url, type) {
    this.Notifications.showComplexDialog({
      template: previewDocumentDialogTemplate,
      size: 'xl',
      resolve: {
        url: () => url,
        type: () => type,
      },
    });
  }

  openUploadFilesDialog() {
    this.Notifications.showComplexDialog({
      backdrop: 'static',
      template: uploadFormModalTemplate,
      bindToController: true,
      controller: UploadFormModalController,
      resolve: {
        serverTime: () => this.api.config.serverTime,
        keywordMaxLength: () => this.api.config.documents.keywordMaxLength,
        locales: () => this.locales,
        locale: () => this.locale,
        categories: () => this.categories,
        jurisdictionId: () => this.jurisdictionId,
        sortCategories: () => this.sortCategories(),
      },
    }).result.then((document) => {
      this.onUploadDocument(document.document).then(() => {
        this.getTags();
        this.getDocuments(this.gridModel.pagingModel);
      });
    });
  }

  openCreateCategoryDialog() {
    this.Notifications.showComplexDialog({
      backdrop: 'static',
      size: null,
      template: categoryDialogTemplate,
      bindToController: true,
      controller: CategoryDialogController,
      resolve: {
        moduleName: () => 'categoryManagement',
        namespace: () => 'documents',
        api: () => this.api.categoryDialogApi,
        category: () => ({
          translations: {},
          jurisdictionId: this.jurisdictionId,
        }),
      },
    }).result.then((category) => {
      this.createCategory(category);
    });
  }

  openEditCategoryDialog(category) {
    this.Notifications.showComplexDialog({
      backdrop: 'static',
      size: null,
      template: categoryDialogTemplate,
      bindToController: true,
      controller: CategoryDialogController,
      resolve: {
        moduleName: () => 'categoryManagement',
        namespace: () => 'documents',
        edit: () => true,
        api: () => this.api.categoryDialogApi,
        category: () => ({
          documentTypeId: category.documentTypeId,
          translations: category.translations,
          jurisdictionId: this.jurisdictionId,
        }),
      },
    }).result.then((category) => {
      this.updateCategory(category);
    });
  }
}

class DocumentsComponent {
  constructor() {
    this.template = documentsTemplate;
    this.controller = DocumentsController;
    this.bindings = {
      appCtrl: '<',
      model: '<',
      namespace: '@',
      moduleName: '@',
      api: '<',
    };
    return this;
  }
}

function getConsts() {
  const gridView = 'grid';
  const tableView = 'table';
  const fileFormats = {
    '.csv': {
      icon: 'fa-file-excel-o',
      allowPreview: false,
    },
    '.xls': {
      icon: 'fa-file-excel-o',
      allowPreview: false,
    },
    '.xlsx': {
      icon: 'fa-file-excel-o',
      allowPreview: false,
    },
    '.rar': {
      icon: 'fa-file-archive-o',
      allowPreview: false,
    },
    '.zip': {
      icon: 'fa-file-archive-o',
      allowPreview: false,
    },
    '.pdf': {
      icon: 'fa-file-pdf-o',
      allowPreview: true,
      previewType: 'pdf',
    },
    '.txt': {
      icon: 'fa-file-text-o',
      allowPreview: false,
    },
    '.doc': {
      icon: 'fa-file-word-o',
      allowPreview: false,
    },
    '.docx': {
      icon: 'fa-file-word-o',
      allowPreview: false,
    },
    '.jpg': {
      icon: 'fa-file-image-o',
      allowPreview: true,
      previewType: 'image',
    },
    '.jpeg': {
      icon: 'fa-file-image-o',
      allowPreview: true,
      previewType: 'image',
    },
    '.gif': {
      icon: 'fa-file-image-o',
      allowPreview: true,
      previewType: 'image',
    },
    '.png': {
      icon: 'fa-file-image-o',
      allowPreview: true,
      previewType: 'image',
    },
    '.bmp': {
      icon: 'fa-file-image-o',
      allowPreview: true,
      previewType: 'image',
    },
    '.tiff': {
      icon: 'fa-file-image-o',
      allowPreview: true,
      previewType: 'image',
    },
    '.aac': {
      icon: 'fa-file-audio-o',
      allowPreview: false,
    },
    '.ogg': {
      icon: 'fa-file-audio-o',
      allowPreview: false,
    },
    '.wma': {
      icon: 'fa-file-audio-o',
      allowPreview: false,
    },
    '.flac': {
      icon: 'fa-file-audio-o',
      allowPreview: false,
    },
    '.mp3': {
      icon: 'fa-file-audio-o',
      allowPreview: false,
    },
    '.mp4': {
      icon: 'fa-file-video-o',
      allowPreview: false,
    },
    '.mpg': {
      icon: 'fa-file-video-o',
      allowPreview: false,
    },
    '.mpeg': {
      icon: 'fa-file-video-o',
      allowPreview: false,
    },
    '.avi': {
      icon: 'fa-file-video-o',
      allowPreview: false,
    },
    '.ppt': {
      icon: 'fa-file-powerpoint-o',
      allowPreview: false,
    },
    '.pptx': {
      icon: 'fa-file-powerpoint-o',
      allowPreview: false,
    },
  };
  return { tableView, gridView, fileFormats };
}

function getDocumentsStateObject() {
  return {
    parent: 'arw',
    url: '/documents',
    template: `<documents
                        app-ctrl="appCtrl"
                        module-name="documentsManagement"
                        namespace="documents"
                        api="$resolve.api"
                    ></documents>`,
    redirectTo: 'permissions-denied',
    params: {
      pageTitle: [
        '$ngRedux',
        'gettextCatalog',
        ($ngRedux, gettextCatalog) => {
          $ngRedux.dispatch(changePageTitle(gettextCatalog.getString('Documents Library')));
        },
      ],
    },
    resolve: {
      api: [
        'appFactory',
        'appApi',
        (appFactory) =>
          appFactory
            .dispatch('documents.loadDictionaries', true)
            .run()
            .then(() => ({
              config: appFactory.deps.config,
              onLanguageSelect: appFactory.dispatch('app.toggleLanguageSelect', true),
              categoryDialogApi: {
                onLanguageSelect: appFactory.dispatch('app.toggleLanguageSelect', true),
                initializeForm: appFactory.dispatch('app.initializeForm', true),
                validateForm: appFactory.dispatch('app.validateForm', true),
                setLanguageValidation: appFactory.actions.app.setLanguageValidation,
              },
            })),
      ],
    },
  };
}

export default angular
  .module('documents', ['documents-errorcodes'])
  .component('documents', new DocumentsComponent())
  .component('documentsFilter', new DocumentsFilterComponent())
  .config([
    '$stateProvider',
    function configure($stateProvider) {
      $stateProvider.state('documents-view', getDocumentsStateObject());
    },
  ]);



// WEBPACK FOOTER //
// ./src/modules/documents/documents-component.js